import React, { FC, useEffect, useState } from "react";
import SortSearchTable from "../../templates/SortSearchTable";
import { Box, Flex, Icon, TableIconButton, Td, Text, Tooltip, Tr, useTranslation } from "@familyzone/component-library";
import Api from "../../../utils/Api";
import { useSessionStore } from "../../../storez/SessionStore";
import dayjs from "dayjs";
import { zIndices } from "../../../utils/ZIndexUtil";
import { DiffView } from "../DiffView";

export interface ConfigAuditRecord {
  id: number;
  scope: string;
  change_timestamp: string;
  change_username: string;
  label: string;
  deviceid: string;
}

const GeneralAudit: FC = () => {
  const { t } = useTranslation();

  const [loaded, setLoaded] = useState<boolean>(false);

  const [activeVersion, setActiveVersion] = useState<number>(0);
  const [data, setData] = useState<ConfigAuditRecord[]>([]);

  const [isDiffModalOpen, setIsDiffModalOpen] = useState<boolean>(false);
  const [diffNewId, setDiffNewId] = useState<number>(0);
  const [diffNewApplianceId, setDiffNewApplianceId] = useState<string>("");
  const [diffOldId, setDiffOldId] = useState<number>(0);
  const [diffOldApplianceId, setDiffOldApplianceId] = useState<string>("");

  const currentDevice = useSessionStore((state) => state.device);

  useEffect(() => {
    load();
  }, []);

  const load = () => {
    setLoaded(false);

    Api.get(
      "/info",
      function (result: { configuration_tracker: { "device.version": number } }) {
        setActiveVersion(result["configuration_tracker"] ? result["configuration_tracker"]["device.version"] : 0);
      },
      (err: string) => {
        console.log(err);
      }
    );

    Api.get("/managedevice/ajax/device/configuration/changes/all", function (result: { data: ConfigAuditRecord[] }) {
      setData(result["data"]);
      setLoaded(true);
    });
  };

  const columns = [
    {
      headerText: t("State"),
      columnName: "state",
      sortable: true,
      searchable: false,
    },
    {
      headerText: t("Time"),
      columnName: "change_timestamp",
      sortable: true,
      searchable: false,
    },
    {
      headerText: t("User"),
      columnName: "change_username",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Scope"),
      columnName: "scope",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Version"),
      columnName: "id",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Operations"),
      columnName: "operations",
      sortable: false,
      searchable: false,
    },
  ];

  const tableDataMap = (record: ConfigAuditRecord, index: number) => (
    <Tr key={index}>
      <Td>
        {activeVersion >= record.id ? (
          <Icon data-testid="icon-fa-check-square" icon="fa-check-square" variant="solid" color="text.paragraph.light" />
        ) : (
          <Icon data-testid="icon-fa-exclamation-circle" icon="fa-exclamation-circle" variant="solid" color="text.paragraph.light" />
        )}
      </Td>
      <Td>
        <Text fontSize="md">{dayjs.unix(parseInt(record.change_timestamp)).format("DD/MM/YYYY, h:mm:ss a")}</Text>
      </Td>
      <Td>
        <Text fontSize="md">{record.change_username}</Text>
      </Td>
      <Td>
        <Text fontSize="md">{record.scope}</Text>
      </Td>
      <Td>
        <Box>
          <Text fontSize="md" display="inline">
            {record.id}
          </Text>
          {currentDevice?.id !== record.deviceid && (
            <Text fontSize="xs" color="red" border="1px solid" display="inline" padding="2px" marginLeft="5px" borderRadius="3px">
              parent
            </Text>
          )}
        </Box>
      </Td>
      <Td>
        <Flex gap="sp8">
          <Tooltip variant="dark" placement="left" hasArrow={true} maxWidth="320px" label={t("Show Changes")} zIndex={zIndices.tooltip}>
            <Box>
              <TableIconButton
                data-testid="show-changes-button"
                disabled={!loaded}
                icon={<Icon icon="fa-file" variant="solid" color="text.paragraph.light" />}
                onClick={() => {
                  onDiff(record);
                }}
                aria-label="Show Changes"
              />
            </Box>
          </Tooltip>
        </Flex>
      </Td>
    </Tr>
  );

  const onDiff = (record: ConfigAuditRecord) => {
    let oldId = 0;
    let oldIdAppliance = "";
    for (const oldRecord of data) {
      if (oldRecord.scope === record.scope && oldId < oldRecord.id && oldRecord.id < record.id) {
        oldId = oldRecord.id;
        oldIdAppliance = oldRecord.deviceid;
      }
    }
    setDiffNewId(record.id);
    setDiffNewApplianceId(record.deviceid);
    setDiffOldId(oldId);
    setDiffOldApplianceId(oldIdAppliance);
    setIsDiffModalOpen(true);
  };

  const onDiffModalClose = () => {
    setIsDiffModalOpen(false);
    setDiffNewId(0);
    setDiffNewApplianceId("");
    setDiffOldId(0);
    setDiffOldApplianceId("");
  };

  return (
    <Box>
      <DiffView
        new_id={diffNewId}
        new_id_device={diffNewApplianceId}
        old_id={diffOldId}
        old_id_device={diffOldApplianceId}
        visible={isDiffModalOpen}
        handleClose={onDiffModalClose}
      />
      <SortSearchTable columns={columns} loaded={loaded} data={data} tableDataMap={tableDataMap} tableMargin="0" />
    </Box>
  );
};

export default GeneralAudit;
